import React from 'react';
import Icon from '../../../components/AppIcon';

const StatusOverview = ({ requests }) => {
  const stats = {
    total: requests?.length,
    pending: requests?.filter(r => r?.status === 'pending')?.length,
    inReview: requests?.filter(r => r?.status === 'in-review')?.length,
    approved: requests?.filter(r => r?.status === 'approved')?.length,
    rejected: requests?.filter(r => r?.status === 'rejected')?.length
  };

  const approvalRate = stats?.total > 0 
    ? Math.round((stats?.approved / (stats?.approved + stats?.rejected)) * 100) || 0
    : 0;

  const avgProcessingTime = "2.3 days"; // Mock data

  const statCards = [
    {
      title: 'Total Requests',
      value: stats?.total,
      icon: 'FileText',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      title: 'Pending',
      value: stats?.pending,
      icon: 'Clock',
      color: 'text-warning',
      bgColor: 'bg-warning/10'
    },
    {
      title: 'In Review',
      value: stats?.inReview,
      icon: 'Eye',
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      title: 'Approved',
      value: stats?.approved,
      icon: 'CheckCircle',
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    {
      title: 'Rejected',
      value: stats?.rejected,
      icon: 'XCircle',
      color: 'text-error',
      bgColor: 'bg-error/10'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {statCards?.map((stat, index) => (
          <div key={index} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat?.bgColor}`}>
                <Icon name={stat?.icon} size={20} className={stat?.color} />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stat?.value}</p>
                <p className="text-sm text-muted-foreground">{stat?.title}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="TrendingUp" size={16} className="text-success" />
            </div>
            <h3 className="text-sm font-medium text-foreground">Approval Rate</h3>
          </div>
          <div className="flex items-end gap-2">
            <span className="text-2xl font-bold text-foreground">{approvalRate}%</span>
            <span className="text-sm text-success mb-1">+5% from last month</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2 mt-2">
            <div 
              className="bg-success h-2 rounded-full transition-all"
              style={{ width: `${approvalRate}%` }}
            />
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="Timer" size={16} className="text-accent" />
            </div>
            <h3 className="text-sm font-medium text-foreground">Avg Processing Time</h3>
          </div>
          <div className="flex items-end gap-2">
            <span className="text-2xl font-bold text-foreground">{avgProcessingTime}</span>
            <span className="text-sm text-success mb-1">-0.5 days from last month</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Target: &lt; 3 days
          </p>
        </div>
      </div>
      {/* Recent Activity */}
      <div className="bg-card border border-border rounded-lg">
        <div className="p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Recent Activity</h3>
        </div>
        <div className="p-4">
          <div className="space-y-3">
            {requests?.slice(0, 5)?.map((request, index) => (
              <div key={index} className="flex items-center gap-3 p-2 hover:bg-muted/30 rounded-lg transition-colors">
                <div className={`w-2 h-2 rounded-full ${
                  request?.status === 'approved' ? 'bg-success' :
                  request?.status === 'rejected' ? 'bg-error' :
                  request?.status === 'in-review' ? 'bg-accent' : 'bg-warning'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">
                    <span className="font-medium">{request?.requester}</span> submitted a change request for{' '}
                    <span className="font-medium">{request?.documentName}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(request.submissionDate)?.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  request?.status === 'approved' ? 'bg-success/10 text-success' :
                  request?.status === 'rejected' ? 'bg-error/10 text-error' :
                  request?.status === 'in-review' ? 'bg-accent/10 text-accent' : 'bg-warning/10 text-warning'
                }`}>
                  {request?.status?.replace('-', ' ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusOverview;